// MapUiReconciler — stellt die Karten-Bedien-UI deklarativ aus MAP_UI_CONFIG her: IDEMPOTENT, pro Karte/Kontext.
//
// Bündelt die heute verstreuten Einzel-Guards (ensureMapToolsMenu + attach, Suchkreis-Sichtbarkeit, Toggle-Spiegelung)
// an EINER Stelle: reconcile(map, context) → Soll-Zustand herstellen. Bei jedem Lebenszyklus-Punkt aufrufbar.
//
// Group-Arten:
//   - `kind:'buttonStack'` (Slice 1): MapToolsMenu mit Items, Soll-Sichtbarkeit/Toggle pro Kontext.
//   - `kind:'module'`     (Slice 2a): eigenständiges Control über `group.factory(...)` → Handle `{ destroy() }`.
//     Im Kontext → einmal bauen (Factory darf `null` liefern wenn „noch nicht bereit" → nächster Reconcile baut);
//     kontextfremd → `destroy()`. (OV-Panel-Modul folgt in Slice 2b.)
// Bewusst dünn: die Group-Primitive (MapToolsMenu / das Modul) bleibt zuständig fürs DOM; der Reconciler orchestriert nur
// Soll-Zustand (Sichtbarkeit/Toggle/Existenz) aus dem Katalog.

import { createMapToolsMenuClass } from "./MapToolsMenu.js";
import { MAP_UI_CONFIG } from "./mapUiConfig.js";
import { resolveControlPosition } from "../adapter/providerConstants.js";
import { str } from "../../i18n/str.js";

export function createMapUiReconciler(google, api) {
    const MapToolsMenu = createMapToolsMenuClass(google);
    const stacks = new Map();    // buttonStack: groupKey -> { menu, buttons: Map<itemKey, btnEl> }
    const modules = new Map();   // module:      groupKey -> Handle { destroy() } | null (null = (noch) nicht gebaut)

    // `contexts` einer Gruppe/eines Items: fehlt → überall; '*' → universell (JEDER Kontext, z.B. mapType/zoom/tilt/
    // overlayPanel — auf allen Karten gleich); Array → nur die gelisteten Kontexte.
    function inContext(contexts, context) {
        if (!contexts || contexts === '*') { return true; }
        return contexts.indexOf(context) !== -1;
    }

    // buttonStack-Group: REBUILD pro Kontext statt „einmal bauen + verbergen". Bei Kontextwechsel (oder wenn die Gruppe
    // ihren Kontext verlässt) den alten Stack ABBAUEN und nur die In-Kontext-Items neu bauen → kein display:none-Geist,
    // kein Leak einer kontextfremden Gruppe (z.B. main-only OV-Button, der sonst in der Galerie stehen bliebe). Gleicher
    // Kontext (Refresh/Tiles-Reload) → nur idempotent re-attachen. Symmetrisch zu ensureModule.
    function reconcileButtonStack(map, group, context) {
        let s = stacks.get(group.key);
        if (s && s.ctx !== context) {
            notifyContextHide(group, s.ctx);   // Seiteneffekte des alten Kontexts lösen (z.B. Suchkreis-Linksklick aus)
            s.menu.destroy();
            stacks.delete(group.key);
            s = null;
        }
        if (!inContext(group.contexts, context)) { return; }   // kontextfremd → nicht (neu) bauen
        if (!s) {
            stacks.set(group.key, buildButtonStack(map, group, context));
        } else {
            s.menu.attach();   // gleicher Kontext → idempotentes Re-Attach nach Refresh/Tiles-Reload (Dedup in MapToolsMenu)
        }
    }

    // Baut den Stack für GENAU diesen Kontext — nur Items, die im Kontext sind (kein verborgener Rest im DOM).
    function buildButtonStack(map, group, context) {
        const menu = new MapToolsMenu(map, { position: group.position });
        const buttons = new Map();
        // Pair nur bilden, wenn ≥2 Mitglieder IM Kontext sind. Sonst steht das einzelne Mitglied normal (kein Pair-Div,
        // kein gedehnter Slot-Füller) — z.B. Galerie: „weitere Archive" fehlt → „zentrieren" ist ein normaler Button.
        const pairCount = {};
        for (const item of group.items) {
            if (item.pair && inContext(item.contexts, context)) { pairCount[item.pair] = (pairCount[item.pair] || 0) + 1; }
        }
        for (const item of group.items) {
            if (!inContext(item.contexts, context)) { continue; }   // out-of-context: gar nicht erst bauen
            const btn = menu.addButton({
                key: item.key,
                svg: item.icon,
                label: item.label ? str(item.label) : undefined,   // Text-Button (bar) statt Icon; label = IF-String-Key wie title
                title: str(item.title),   // item.title = IF-String-Key → zur Laufzeit aufgelöst (topo bereit)
                toggle: !!item.toggle,
                size: item.size,          // Chef 14: optionale Größen-Variante ('small' | …) → topo2-btn--<size>
                pair: (item.pair && pairCount[item.pair] >= 2) ? item.pair : undefined,   // Chef 5: nur paaren wenn ≥2 im Kontext
                onClick: item.onClick ? () => item.onClick(api) : undefined,
                onToggle: item.onToggle ? (active) => item.onToggle(active, api) : undefined
            });
            buttons.set(item.key, btn);
        }
        return { menu, buttons, ctx: context };
    }

    // Beim Verlassen eines Kontexts die onContextHide-Seiteneffekte der dort aktiv gewesenen Items lösen (idempotent).
    function notifyContextHide(group, oldCtx) {
        for (const item of group.items) {
            if (typeof item.onContextHide === 'function' && inContext(item.contexts, oldCtx)) {
                item.onContextHide(api);
            }
        }
    }

    // module-Group: im Kontext einmal bauen (Factory darf null = „noch nicht bereit" liefern → Retry beim nächsten
    // Reconcile), kontextfremd sauber entfernen (Handle.destroy). Ersetzt verstreute Ad-hoc-Removes.
    function ensureModule(map, group, context) {
        const want = inContext(group.contexts, context);
        const handle = modules.get(group.key) || null;

        if (!want) {
            if (handle && typeof handle.destroy === 'function') { handle.destroy(); }
            if (handle) { modules.set(group.key, null); }
            return;
        }
        if (handle) {
            if (typeof handle.attach === 'function') { handle.attach(); }   // optionales idempotentes Re-Attach
            return;
        }
        const position = group.position ? resolveControlPosition(google, group.position) : undefined;
        const built = typeof group.factory === 'function' ? group.factory({ map, api, context, google, position }) : null;
        modules.set(group.key, built || null);
    }

    // #1 Autoritatives Lesen: die Toggle-Ringe spiegeln den AKTUELLEN Kontext-Store (state[item.activeFromState]) statt
    // ein Event. Eine Subscription auf den jeweiligen Kontext-Store; bei Kontextwechsel umgehängt. setActive feuert KEIN
    // onToggle → kein Loop.
    let ringStoreUnsub = null;
    let ringStoreCtx = null;

    function updateRings(state) {
        for (const group of MAP_UI_CONFIG.groups) {
            if (group.kind !== 'buttonStack') { continue; }
            const s = stacks.get(group.key);
            if (!s) { continue; }
            for (const item of group.items) {
                const btn = s.buttons.get(item.key);
                if (!btn) { continue; }   // Item nicht im aktuellen Kontext gebaut (Rebuild-Modell)
                if (item.activeFromState) { s.menu.setActive(btn, !!state[item.activeFromState]); }
                // Bedingte Sichtbarkeit aus dem Store (z.B. Suchkreis-Löschen nur bei gezogenem Kreis; Overlays-Button nur
                // wenn es Overlays gibt). Array-Wert → nach Länge (leeres Array = aus), sonst Boolean.
                if (item.visibleFromState) {
                    const vfs = state[item.visibleFromState];
                    btn.style.display = (Array.isArray(vfs) ? vfs.length > 0 : !!vfs) ? '' : 'none';
                }
            }
        }
    }

    function bindRingsToStore(context) {
        const store = (api && api.getStore) ? api.getStore(context) : null;
        if (!store) { return; }
        if (ringStoreCtx !== context) {
            if (ringStoreUnsub) { ringStoreUnsub(); ringStoreUnsub = null; }
            ringStoreUnsub = store.subscribe((state) => updateRings(state));
            ringStoreCtx = context;
        }
        updateRings(store.get());   // sofort aus dem aktuellen Store spiegeln
    }

    return {
        // Map-UI-Unify (Chef 16): Zugriff auf ein gebautes Modul-Handle (z.B. overlayPanel.toggle()) für api-Aktionen.
        getModule(key) { return modules.get(key) || null; },
        // Erzwingt frischen Aufbau eines Moduls beim nächsten reconcile — für Datenwechsel, die der Reconciler nicht
        // selbst sieht (z.B. neue Galerie → andere Marker). Entfernt das aktuelle Control sofort.
        invalidateModule(key) {
            const handle = modules.get(key) || null;
            if (handle && typeof handle.destroy === 'function') { handle.destroy(); }
            modules.set(key, null);
        },
        // Soll-Zustand der Karten-UI für diesen Kontext herstellen.
        reconcile(map, context) {
            if (!map) { return; }
            for (const group of MAP_UI_CONFIG.groups) {
                if (group.kind === 'buttonStack') {
                    reconcileButtonStack(map, group, context);   // unbedingt — baut In-Kontext neu auf, entfernt kontextfremde
                } else if (group.kind === 'module') {
                    ensureModule(map, group, context);   // unbedingt aufrufen — entfernt auch kontextfremde Module
                }
            }
            bindRingsToStore(context);   // #1: Toggle-Ringe aus dem Kontext-Store spiegeln
        }
    };
}
